import { useState, useEffect } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import OTPInput from 'otp-input-react';
import { validateGoogleIdToken } from '@Helpers/googleAuth/validateGoogleIdToken';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import bcrypt from 'bcryptjs';
import { useLocation } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as KaizIQLogoNoTextWhite } from '@Assets/kaiziqLogoNoTextWhite.svg';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { BiShow, BiHide } from 'react-icons/bi';
import { MdLock } from 'react-icons/md';
import { BsPerson as User } from 'react-icons/bs';
import { useNavigateWithRedirect } from '@Hooks/useNavigateWithRedirect';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import '@Partials/_ComStyles.scss';
import './LoginPage.scss';

const GOOGLE_CLIENT_ID = '704888664600-l1g177ip7drg2060329ievengm13o6j0.apps.googleusercontent.com';

function sanitizePhoneNumber(input) {
    return input.replace(/^[+]+/, '').replace(/[\s-,]/g, '');
}

function getLoginErrorMessage(retToken) {
    const platform = retToken?.existingPlatform;
    const platformLabel = platform
        ? platform.charAt(0).toUpperCase() + platform.slice(1)
        : 'social login';

    switch (retToken?.failureReason) {
        case 'USER_NOT_FOUND':
            return 'No account found for that email, username, or phone number.';
        case 'INVALID_CREDENTIALS':
            return 'Incorrect credentials. Please check your password or verification code and try again.';
        case 'INVALID_ENTRY':
            return 'Please enter a valid email address, username, or phone number.';
        case 'PASSWORD_REQUIRED':
            return 'Please enter your password to continue.';
        case 'OTP_REQUIRED':
            return 'Please enter the verification code to continue.';
        case 'ACCOUNT_CONFLICT':
            switch (retToken?.conflictType) {
                case 'EXISTS_MANUAL_WHEN_SOCIAL':
                    return 'This account uses a password. Sign in with your password, or use "Forgot password" to reset it.';
                case 'EXISTS_SOCIAL_WHEN_MANUAL':
                    return `This account was created with ${platformLabel}. Please use the "${platformLabel}" sign-in option above.`;
                case 'EXISTS_OTHER_SOCIAL_PLATFORM':
                    return `This account is linked to ${platformLabel}. Please sign in with ${platformLabel} instead.`;
                default:
                    return retToken?.message || 'There is a conflict with the login method for this account. Please try a different sign-in option.';
            }
        default:
            return retToken?.message || 'We could not sign you in. Please try again.';
    }
}

export default function LoginPage({ setToken, setEntryPage }) {

    const className = 'loginPage';
    const [username, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [searchParams] = useSearchParams();
    const [cursor, setCursor] = useState('auto');
    const [invalidPassword, setInvalidPassword] = useState(false);
    const [, setLoginInvalid] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [googleErrors, setGoogleErrors] = useState([]);
    const [preload, setPreload] = useState(true);
    const [phoneError, setPhoneError] = useState(null);
    const [loginStep, setLoginStep] = useState(1);
    const [manualLoginMethod, setManualLoginMethod] = useState(null);
    const [errorLoggingUser, setErrorLoggingUser] = useState(null);
    const [showInvalidLoginPopup, setShowInvalidLoginPopup] = useState(false);
    const [checkingLoginMethod, setCheckingLoginMethod] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpResendCooldown, setOtpResendCooldown] = useState(0);
    const [otpError, setOtpError] = useState(null);
    const [, setExistingPlatform] = useState(null);
    const [, setConflictType] = useState(null);
    const isBusy = cursor === 'wait' || checkingLoginMethod || otpSending;
    const { setActiveUserJourney, setActiveUserPermission, setActiveUserAccessList, setActiveUser, setActiveUserPreferencesAll, setUserType } = useReduxActiveUser();
    const { setCurrentOrg, clearCurrentOrg: clearOrgValue } = useReduxOrg();
    const { clearCurrentSubscription } = useReduxSubscription();
    const { setCurrentProject } = useReduxProject();
    const navigate = useNavigateWithRedirect();
    const location = useLocation();
    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();

    const handleLogin = async (e, authPayload) => {
        e?.preventDefault();
        setCursor('wait');
        try {
            let activeUserJourney, userAccessListObj;
            const retToken = await userAuthService.login(authPayload);
            if (!!retToken.token && retToken.token !== 'void') {
                setActiveUser(JSON.parse(retToken?.activeUser));
                setActiveUserPermission(JSON.parse(retToken?.activeUserPermissions));
                if(!!retToken?.activeUserPreferences){
                    setActiveUserPreferencesAll(JSON.parse(retToken?.activeUserPreferences));
                } else {
                    setActiveUserPreferencesAll({});
                }
                if(!!retToken.activeUserJourney){
                    setActiveUserJourney(activeUserJourney);
                } else {
                    setActiveUserJourney({});
                }
                userAccessListObj = await userAuthService.getAccessListForUser(retToken?.activeUserId);
                userAccessListObj = JSON.parse(userAccessListObj.body);
                setActiveUserAccessList(userAccessListObj);
            } else {
                setLoginInvalid(true);
                setConflictType(retToken?.conflictType || null);
                setExistingPlatform(retToken?.existingPlatform || null);
                setErrorLoggingUser(getLoginErrorMessage(retToken));
                setShowInvalidLoginPopup(true);
                if (retToken?.conflictType === 'EXISTS_SOCIAL_WHEN_MANUAL' || retToken?.conflictType === 'EXISTS_OTHER_SOCIAL_PLATFORM') {
                    setLoginStep(1);
                    setManualLoginMethod(null);
                }
            }
            if(!!retToken && retToken?.token !== 'void') {
                const isAdmin = searchParams.get('loginType') === 'admin';
                console.log(isAdmin);
                setUserType(isAdmin ? 'admin' : 'reporter');
                setToken(retToken);
                if (isAdmin) {
                    clearOrgValue();
                    clearCurrentSubscription();
                    const resolveOrgs = (userAccessListObj?.orgs || []).filter(o => o.visibility === 'resolve');
                    console.log(resolveOrgs);
                    if (resolveOrgs.length === 0) {
                        navigate('/onboarding', { replace: true });
                    } else if (resolveOrgs.length === 1) {
                        await setCurrentOrg(resolveOrgs[0]);
                        const resolveProject = (userAccessListObj?.projects || []).find(
                            p => p.visibility === 'resolve' && p.parentId === resolveOrgs[0].id
                        );
                        if (resolveProject) await setCurrentProject(resolveProject);
                        navigate('/dashboard', { replace: true });
                    } else {
                        navigate('/org-select', { replace: true });
                    }
                } else {
                    const from = location.state?.from || { pathname: '/home', search: '' };
                    navigate(from, { replace: true });
                }
                return;
            }
        } catch (err) {
            setLoginInvalid(true);
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handlePasswordCheck = async (e) => {
        e?.preventDefault();
        if(!username || username.trim() === '') return;
        setErrorLoggingUser(null);
        setCheckingLoginMethod(true);
        setIsFading(true);
        setTimeout(async () => {
            try {
                let entryValue = username;
                const phoneRegex = /^(\+?\d[\d\s\-(),]{7,})$/;
                if (phoneRegex.test(username.trim())) {
                    entryValue = sanitizePhoneNumber(username.trim());
                }
                let body = {
                    'entry': entryValue
                };
                const resp = await userAuthService.getUserLoginMethod(body);
                if(resp && Object.prototype.hasOwnProperty.call(resp, 'loginMethod')) {
                    let loginMethod = resp.loginMethod;
                    if(loginMethod && (loginMethod === 'password' || loginMethod === 'otp' ||
                        loginMethod === 'passwordVerified' || loginMethod === 'otpVerified'   )) {
                        setManualLoginMethod(loginMethod);
                        setLoginStep(2);
                        if(loginMethod === 'otp' || loginMethod === 'otpVerified') {
                            sendOtp(username);
                        }
                    } else {
                        setManualLoginMethod(null);
                        setLoginStep(1);
                        setErrorLoggingUser('Invalid login details. Please check your details and try again.');
                        setShowInvalidLoginPopup(true);
                    }
                } else {
                    setManualLoginMethod(null);
                    setLoginStep(1);
                    setErrorLoggingUser('Could not determine login method (server issue). Please try again later.');
                }
            } catch(err) {
                setManualLoginMethod(null);
                setLoginStep(1);
                setErrorLoggingUser('Unable to determine login method right now. Please try again later.');
                setShowInvalidLoginPopup(true);
            } finally {
                setCheckingLoginMethod(false);
                requestAnimationFrame(() => setIsFading(false));
            }
        }, 300);
    };

    const signup = async e => {
        e?.preventDefault();
        setEntryPage('signup');
    }

    const forgotPassword = async e => {
        e?.preventDefault();
        setEntryPage('forgotPassword');
    }

    const handleResendOtp = async (e) => {
        e?.preventDefault();
        if (otpResendCooldown > 0) return;
        try {
            await (userAuthService.sendOtp ? userAuthService.sendOtp({ username }) : Promise.resolve(true));
            setOtpResendCooldown(30);
        } catch (err) {
            setOtpError('Failed to resend OTP.');
        }
    };

    const sendOtp = async (entry) => {
        setOtpError(null);
        setOtpSending(true);
        try {
            let body = {
                entry: entry
            };
            await userAuthService.sendLoginOTP(body);
            setOtpSent(true);
            setOtpResendCooldown(30);
        } catch(err) {
            setOtpError('Failed to send OTP. Please try again.');
        } finally {
            setOtpSending(false);
        }
    };

    const handleGoogleSuccess = (credentialResponse) => {
        const rawCred = credentialResponse.credential || '';
        const { valid, errors, payload } = validateGoogleIdToken(rawCred, { expectedAud: GOOGLE_CLIENT_ID });
        setGoogleErrors(errors);
        if(!valid) return;
        const googleEmail = payload.email;
        const fullName = payload.name || (payload.given_name && payload.family_name ? payload.given_name + ' ' + payload.family_name : undefined);
        if(!googleEmail || !fullName) {
            setErrorLoggingUser('Google Sign-In Failed');
            return;
        }
        const loginObject = {
            entry: googleEmail,
            fullName: fullName,
            loginMethod: 'social',
            socialPlatform: 'google',
            sub: payload.sub
        };
        handleLogin(null, loginObject);
    };

    const handleSubmit = async e => {
        e?.preventDefault();
        const loginObject = {
            loginMethod: manualLoginMethod
        };
        if((manualLoginMethod === 'otp') || (manualLoginMethod === 'otpVerified')) {
            loginObject.otp = otp;
            loginObject.entry = username;
        }
        if((manualLoginMethod === 'password') || (manualLoginMethod === 'passwordVerified')) {
            loginObject.entry = username;
            loginObject.password = password;
        }
        handleLogin(e, loginObject);
    }

    const handleGoogleFailure = () => {
        setErrorLoggingUser('Google Sign-In Failed');
    };

    const backToIdentifier = (e) => {
        e?.preventDefault();
        setIsFading(true);
        setManualLoginMethod(null);
        setTimeout(() => {
            setLoginStep(1);
            requestAnimationFrame(() => setIsFading(false));
        }, 300);
    };

    const switchManualLoginMethod = (e) => {
        e?.preventDefault();
        let nextLogin = 'otp';
        switch(manualLoginMethod) {
            case 'password':
                nextLogin = 'otp';
                sendOtp(username);
                break;
            case 'passwordVerified':
                nextLogin = 'otpVerified';
                sendOtp(username);
                break;
            case 'otp' :
                nextLogin = 'password';
                break;
            case 'otpVerified':
                nextLogin = 'passwordVerified';
                break;
        }
        setManualLoginMethod(nextLogin);
    };

    useEffect(() => {
        setPreload(false);
    }, []);

    useEffect(() => {
        if (otpResendCooldown <= 0) return;
        const t = setTimeout(() => setOtpResendCooldown(otpResendCooldown - 1), 1000);
        return () => clearTimeout(t);
    }, [otpResendCooldown]);

    const handleUserNameChange = (e) => {
        let sanitizedUserName = DOMPurify.sanitize(e.target.value);
        setUserName(sanitizedUserName);
        setShowInvalidLoginPopup(false);
        setErrorLoggingUser(null);

        const phoneRegex = /^(\+?\d[\d\s\-()]{7,})$/;
        if (phoneRegex.test(sanitizedUserName.trim())) {
            if (!sanitizedUserName.trim().startsWith('+')) {
                setPhoneError('Please enter your phone number with country code (e.g. +91XXXXXXXXXX)');
            } else {
                setPhoneError(null);
            }
        } else {
            setPhoneError(null);
        }
    }

    const handlePasswordChange = (e) => {
        setShowInvalidLoginPopup(false);
        setErrorLoggingUser(null);
        let sanitizedPassword = DOMPurify.sanitize(e.target.value);
        if(sanitizedPassword.length === 0) {
            setInvalidPassword(true);
        } else {
            setInvalidPassword(false);
        }
        let salt = '$2a$10$CwTycUXWue0Thq9StjUM0u'
        let hashedPassword = bcrypt.hashSync(sanitizedPassword, salt)
        setPassword(hashedPassword);
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    }

    return (
        <div className={className} style={{ cursor }}>
            {/* Background Elements */}
            <div className={className + '__background'}>
                <div className={className + '__background__gradient'}></div>
                <div className={className + '__background__pattern'}></div>
                <div className={className + '__background__shapes'}>
                    <div className={className + '__background__shape --shape1'}></div>
                    <div className={className + '__background__shape --shape2'}></div>
                    <div className={className + '__background__shape --shape3'}></div>
                </div>
            </div>

            {/* Main Container */}
            <div className={className + '__container'}>
                {/* Left Side - Branding */}
                <div className={className + '__branding'}>
                    <div className={className + '__branding__content'}>
                        <div className={className + '__branding__logo'}>
                            <KaizIQText className={className + '__branding__logoSvg'} />
                        </div>
                        <div className={className + '__branding__hero'}>
                            <h1 className={className + '__branding__title'}>
                                Transform Your
                                <span className={className + '__branding__titleAccent'}> Service Desk</span>
                            </h1>
                            <p className={className + '__branding__subtitle'}>
                                Intelligent ticket management and resolution tracking for high-performing support teams
                            </p>
                        </div>
                        <div className={className + '__branding__features'}>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Smart Tracking</strong>
                                    <span>Monitor and resolve issues with powerful analytics</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Fast Resolution</strong>
                                    <span>Streamline workflows to maximize team efficiency</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature flex-row-center-1rem-gap'}>
                                <KaizIQLogoNoTextWhite className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Customer Satisfaction</strong>
                                    <span>Deliver exceptional support experiences consistently</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={className + '__formSection'}>
                    <div className={className + '__formContainer' +
                        (preload ? ' ' + className + '__formContainer--preload' : '') +
                        (isFading ? ' ' + className + '__formContainer--fadeOut' : '')}>
                        {/* Mobile-only Logo (shown on phone screens) */}
                        <div className={className + '__mobileLogo'}>
                            <KaizIQText className={className + '__branding__logoSvg'} />
                        </div>

                        {/* Header */}
                        <div className={className + '__header'}>
                            <h2 className={className + '__welcomeTitle'}>Welcome Back!</h2>
                            <p className={className + '__welcomeSubtitle grey-text'}>
                                {loginStep === 1 ? 'Sign in to continue to KaizIQ' : 'Complete your authentication'}
                            </p>
                        </div>

                        {/* Step 1: Identifier Entry */}
                        {loginStep === 1 && (
                            <div className={className + '__step1'}>
                                {/* Google Login */}
                                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleFailure}
                                        useOneTap={false}
                                        text='continue_with'
                                        logo_alignment='center'
                                        shape='circle'
                                        size='medium'
                                        outline='none'/>
                                </GoogleOAuthProvider>

                                {googleErrors.length > 0 && (
                                    <div className={className + '__error'}>
                                        Google auth issues: {googleErrors.join(', ')}
                                    </div>
                                )}

                                {/* Divider */}
                                <div className={className + '__divider'}>
                                    <span className={className + '__dividerLine'}></span>
                                    <span className={className + '__dividerText grey-text sm-text'}>OR</span>
                                    <span className={className + '__dividerLine'}></span>
                                </div>

                                {/* Manual Login Form */}
                                <form onSubmit={handlePasswordCheck} className={className + '__form'}>
                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Email, Username or Phone</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <User className={className + '__inputIcon'} />
                                            <input
                                                type='text'
                                                className={className + '__input'}
                                                placeholder='Enter your email, username or phone'
                                                onChange={handleUserNameChange}
                                                disabled={cursor === 'wait'}
                                                value={username || ''}
                                            />
                                        </div>
                                        {phoneError && (
                                            <div className={className + '__fieldError'}>
                                                {phoneError}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={!username || username.trim() === '' || cursor === 'wait'}
                                    >
                                        {checkingLoginMethod ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Checking...
                                            </>
                                        ) : 'Continue'}
                                    </button>
                                </form>

                                {showInvalidLoginPopup && errorLoggingUser && (
                                    <div className={className + '__loginError alert-text mt-10'}>
                                        {errorLoggingUser}
                                    </div>
                                )}

                                {/* Footer Links */}
                                <div className={className + '__footer flex-row-center-1rem-gap sm-text'}>
                                    <div className={className + '__footer__t1 grey-text'}>
                                        Don't have an account?
                                    </div>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={signup}>
                                        Sign Up
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Password/OTP Entry */}
                        {loginStep === 2 && (
                            <div className={className + '__step2'}>
                                {/* User Summary */}
                                <div className={className + '__userSummary flex-row-center-space-between'}>
                                    <div className={className + '__userSummaryText sm-text'}>
                                        Signing in as <strong>{username}</strong>
                                    </div>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={backToIdentifier}>
                                        Not you?
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className={className + '__form'}>
                                    {/* Password Input */}
                                    {(manualLoginMethod === 'password' || manualLoginMethod === 'passwordVerified') && (
                                        <div className={className + '__inputGroup'}>
                                            <label className={className + '__label grey-text sm-text'}>Password</label>
                                            <div className={className + '__inputWrapper flex-row-center'}>
                                                <MdLock className={className + '__inputIcon'} />
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    className={className + '__input'}
                                                    placeholder='Enter your password'
                                                    onChange={handlePasswordChange}
                                                    disabled={cursor === 'wait'}
                                                />
                                                <IconButton
                                                    className={className + '__togglePassword'}
                                                    onClick={togglePasswordVisibility}
                                                    size="small"
                                                >
                                                    {showPassword ? <BiHide /> : <BiShow />}
                                                </IconButton>
                                            </div>
                                        </div>
                                    )}

                                    {/* OTP Input */}
                                    {(manualLoginMethod === 'otp' || manualLoginMethod === 'otpVerified') && (
                                        <div className={className + '__otpSection flex-column-center gap-1rem'}>
                                            <label className={className + '__label'}>Verification Code</label>
                                            <p className={className + '__otpDescription sm-text grey-text'}>
                                                Enter the 6-digit code sent to your email
                                                {(manualLoginMethod === "passwordVerified" || manualLoginMethod === "otpVerified") &&
                                                    ' and phone number'}
                                            </p>
                                            <div className={className + '__otpContainer'}>
                                                <OTPInput
                                                    value={otp}
                                                    onChange={setOtp}
                                                    autoFocus
                                                    OTPLength={6}
                                                    otpType='number'
                                                    disabled={otpSending || cursor === 'wait'}
                                                    inputClass={className + '__otpInput'}
                                                    className={className + '__otpWrapper'}
                                                />
                                            </div>
                                            <div className={className + '__otpFooter flex-row-center-0p5rem-gap'}>
                                                {otpSending && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>
                                                        <div className={className + '__spinner'}></div>
                                                        Sending code...
                                                    </span>
                                                )}
                                                {!otpSending && otpSent && otpResendCooldown > 0 && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>
                                                        Resend code in {otpResendCooldown}s
                                                    </span>
                                                )}
                                                {!otpSending && otpSent && otpResendCooldown === 0 && (
                                                    <button className={className + '__linkBtn sm-text hovered'} onClick={handleResendOtp}>
                                                        Resend code
                                                    </button>
                                                )}
                                                {!otpSent && !otpSending && (
                                                    <span className={className + '__otpStatus flex-row-center grey-text sm-text'}>Preparing verification...</span>
                                                )}
                                            </div>
                                            {otpError && (
                                                <div className={className + '__fieldError alert-text mt-10'}>
                                                    {otpError}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={cursor === 'wait' ||
                                            (manualLoginMethod === 'password' && (!password || invalidPassword)) ||
                                            (manualLoginMethod === 'otp' && (otp.length !== 6 || otpSending))}
                                    >
                                        {cursor === 'wait' ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Signing in...
                                            </>
                                        ) : 'Sign In'}
                                    </button>
                                </form>

                                {showInvalidLoginPopup && errorLoggingUser && (
                                    <div className={className + '__loginError alert-text mt-10'}>
                                        {errorLoggingUser}
                                    </div>
                                )}

                                {/* Alternative Login Method */}
                                <div className={className + '__alternativeLogin'}>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={switchManualLoginMethod}>
                                        {(manualLoginMethod === 'password' || manualLoginMethod === 'passwordVerified')
                                            ? 'Use verification code instead'
                                            : 'Use password instead'}
                                    </button>
                                </div>

                                {/* Footer Links */}
                                <div className={className + '__footer flex-row-center'}>
                                    <button className={className + '__linkBtn sm-text hovered'} onClick={forgotPassword}>
                                        Forgot password?
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            {/* Loading Overlay */}
            {checkingLoginMethod && (
                <div className={className + '__loadingOverlay'}>
                    <div className={className + '__loadingContent'}>
                        <div className={className + '__loadingSpinner'}></div>
                        <p className={className + '__loadingText'}>Checking login method...</p>
                    </div>
                </div>
            )}
            </div>
        </div>
    )
}

LoginPage.propTypes = {
    setToken: PropTypes.func.isRequired
};
